import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail, ORIGIN } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, ArrowRight, Loader2, Sparkles, PauseCircle, Play, FileArchive, Download, Mail, ChevronDown, Upload, Coins } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [pausedCount, setPausedCount] = useState(0);
  const [resuming, setResuming] = useState(false);
  const [bundle, setBundle] = useState(null); // account-wide PDF bundle job
  const [bundleStarting, setBundleStarting] = useState(false);
  const [bundleEmail, setBundleEmail] = useState(false);
  const [autoBuilding, setAutoBuilding] = useState(false);
  const [billing, setBilling] = useState(null);
  const [form, setForm] = useState({
    title: "",
    purpose: "",
    tone: "warm, editorial",
    audience: "",
    default_mode: "double_espresso",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch {
      setProjects([]);
    }
  };

  useEffect(() => {
    load();
    api.get("/jobs/paused-count").then((r) => setPausedCount(r.data.count)).catch(() => {});
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    // Carry the homepage "What are you making?" idea straight into a ready-to-generate
    // project: create it, auto-build the master index from the idea, then open the workspace.
    const draft = sessionStorage.getItem("espresso_draft");
    if (draft) {
      sessionStorage.removeItem("espresso_draft");
      autoStart(draft.trim());
    }
    api.get("/exports/all-pdf-latest").then((r) => {
      if (r.data.job_id) {
        setBundle(r.data);
        if (["queued", "building"].includes(r.data.status)) pollBundle(r.data.job_id);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const pollBundle = async (jobId) => {
    const deadline = Date.now() + 60 * 60 * 1000; // up to 1h — big libraries take a while
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/export-jobs/${jobId}`);
        setBundle((prev) => ({ job_id: jobId, startedAt: prev?.startedAt || Date.now(), ...data }));
        if (data.status === "ready" || data.status === "error") return;
      } catch {
        /* keep polling through transient errors */
      }
    }
  };

  const startBundle = async (fmt = "pdf") => {
    setBundleStarting(true);
    try {
      const params = new URLSearchParams({ format: fmt });
      if (bundleEmail) params.set("email", "true");
      const { data } = await api.post(`/exports/all-pdf-async?${params.toString()}`);
      const label = { pdf: "PDF", epub: "EPUB", docx: "Word" }[fmt] || fmt.toUpperCase();
      toast.success(`Building your ${label} bundle — this runs in the background.`);
      setBundle({ job_id: data.job_id, status: data.status || "queued", progress: null, startedAt: Date.now(), bundle_format: fmt });
      pollBundle(data.job_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBundleStarting(false);
    }
  };

  const bundleBusy = bundleStarting || ["queued", "building"].includes(bundle?.status);

  const resumeAll = async () => {
    setResuming(true);
    try {
      const { data } = await api.post("/jobs/resume-all");
      toast.success(`Resumed ${data.resumed} paused job${data.resumed === 1 ? "" : "s"}`);
      setPausedCount(0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResuming(false);
    }
  };

  const create = async () => {
    if (!form.title.trim()) {
      toast.error("Please give your project a title");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project created");
      navigate(`/project/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const autoStart = async (draft) => {
    setAutoBuilding(true);
    try {
      const title = draft.length > 70 ? draft.slice(0, 67).trimEnd() + "…" : draft;
      const { data: proj } = await api.post("/projects", {
        title,
        purpose: draft,
        tone: "warm, editorial",
        audience: "",
        default_mode: "double_espresso",
      });
      try {
        await api.post(`/projects/${proj.id}/generate-index`, { prompt: draft });
        toast.success("Your master index is ready — start generating.");
      } catch {
        toast.message("Project created — build your index on the next screen.");
      }
      navigate(`/project/${proj.id}`);
    } catch (err) {
      setAutoBuilding(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startIngest = async () => {
    setIngesting(true);
    try {
      const { data: proj } = await api.post("/projects", {
        title: "Untitled knowledge base",
        purpose: "",
        tone: "warm, editorial",
        default_mode: "double_espresso",
      });
      navigate(`/project/${proj.id}?tab=index`);
    } catch (err) {
      setIngesting(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const loadDemo = async () => {
    setLoadingDemo(true);
    try {
      const { data } = await api.post("/projects/demo");
      toast.success("Demo publication ready");
      navigate(`/project/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoadingDemo(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      {autoBuilding && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm"
          data-testid="auto-build-overlay"
        >
          <Loader2 size={40} className="animate-spin text-primary" />
          <p className="mt-6 font-display text-2xl tracking-tight">Spinning up the processor…</p>
          <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">
            Creating your project and decomposing your idea into a structured knowledge blueprint. This takes a few moments.
          </p>
        </div>
      )}
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Your projects</h1>
            <p className="mt-2 text-muted-foreground">
              Every publication begins as a prompt, an index, or an ingested knowledge base.
            </p>
            {billing && (
              <div className="mt-4" data-testid="plan-badge">
                {billing.unlimited ? (
                  <span className="inline-flex items-center gap-2 rounded-sm border border-[hsl(var(--matcha))] bg-[hsl(var(--matcha)/0.1)] px-3 py-1.5 text-sm" data-testid="plan-badge-unlimited">
                    <Coins size={15} className="text-[hsl(var(--matcha))]" />
                    <span className="font-medium">Unlimited credits</span>
                  </span>
                ) : billing.subscription_active ? (
                  <Link
                    to="/billing"
                    className="inline-flex items-center gap-2 rounded-sm border border-[hsl(var(--matcha))] bg-[hsl(var(--matcha)/0.08)] px-3 py-1.5 text-sm transition-colors hover:bg-[hsl(var(--matcha)/0.16)]"
                    data-testid="plan-badge-subscriber"
                  >
                    <Coins size={15} className="text-[hsl(var(--matcha))]" />
                    <span className="font-medium capitalize">{billing.subscription} plan</span>
                    <span className="text-muted-foreground">· {billing.balance.toLocaleString()} credits</span>
                  </Link>
                ) : (
                  <Link
                    to="/pricing"
                    className="group inline-flex items-center gap-2 rounded-sm border border-[hsl(var(--teal))]/50 bg-[hsl(var(--teal)/0.06)] px-3 py-1.5 text-sm transition-colors hover:border-[hsl(var(--teal))] hover:bg-[hsl(var(--teal)/0.12)]"
                    data-testid="plan-badge-free"
                  >
                    <Coins size={15} className="text-[hsl(var(--teal))]" />
                    <span className="text-muted-foreground">{billing.balance.toLocaleString()} credits left</span>
                    <span className="font-medium text-[hsl(var(--teal))]">Upgrade your plan</span>
                    <ArrowRight size={14} className="text-[hsl(var(--teal))] transition-transform group-hover:translate-x-0.5" />
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {projects && projects.length > 0 && (
              <div className="flex flex-col items-end gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-sm"
                      disabled={bundleBusy}
                      data-testid="download-all-pdf-button"
                    >
                      {bundleBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileArchive size={16} className="mr-2" />}
                      Download all
                      <ChevronDown size={15} className="ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-sm">
                    <DropdownMenuItem onClick={() => startBundle("pdf")} data-testid="bundle-format-pdf">
                      As PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => startBundle("epub")} data-testid="bundle-format-epub">
                      As EPUB
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => startBundle("docx")} data-testid="bundle-format-docx">
                      As Word (DOCX)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="bundle-email-label">
                  <Checkbox
                    checked={bundleEmail}
                    onCheckedChange={(v) => setBundleEmail(!!v)}
                    className="h-3.5 w-3.5"
                    data-testid="bundle-email-checkbox"
                  />
                  <Mail size={12} className="text-primary" /> Email me when ready
                </label>
              </div>
            )}
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={loadDemo}
              disabled={loadingDemo}
              data-testid="load-demo-button"
            >
              {loadingDemo ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
              Try the demo
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-project-button">
                <Plus size={16} className="mr-2" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Create a project</DialogTitle>
                <DialogDescription>
                  Define your publication. You'll build the structure next.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="p-title">Title</Label>
                  <Input
                    id="p-title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. The Human Body"
                    className="rounded-sm"
                    data-testid="project-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-purpose">Purpose</Label>
                  <Textarea
                    id="p-purpose"
                    value={form.purpose}
                    onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                    placeholder="What is this publication for?"
                    className="rounded-sm"
                    data-testid="project-purpose-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="p-tone">Tone</Label>
                    <Input
                      id="p-tone"
                      value={form.tone}
                      onChange={(e) => setForm({ ...form, tone: e.target.value })}
                      className="rounded-sm"
                      data-testid="project-tone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="p-audience">Audience</Label>
                    <Input
                      id="p-audience"
                      value={form.audience}
                      onChange={(e) => setForm({ ...form, audience: e.target.value })}
                      placeholder="e.g. beginners"
                      className="rounded-sm"
                      data-testid="project-audience-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Default generation mode</Label>
                  <Select
                    value={form.default_mode}
                    onValueChange={(v) => setForm({ ...form, default_mode: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="project-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="espresso">Espresso — fast & economical</SelectItem>
                      <SelectItem value="double_espresso">Double Espresso — balanced</SelectItem>
                      <SelectItem value="ristretto">Ristretto — max reasoning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={create}
                  disabled={creating}
                  className="rounded-sm"
                  data-testid="create-project-submit"
                >
                  {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  Create project
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mt-10">
          {bundle && bundle.status && (
            <div
              className={`mb-6 flex flex-col gap-3 rounded-sm border px-5 py-4 ${
                bundle.status === "ready"
                  ? "border-primary/40 bg-primary/5"
                  : bundle.status === "error"
                  ? "border-destructive/40 bg-destructive/10"
                  : "border-border bg-secondary/40"
              }`}
              data-testid="bundle-banner"
            >
              <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {bundleBusy ? (
                  <Loader2 size={20} className="animate-spin text-muted-foreground" />
                ) : (
                  <FileArchive size={20} className={bundle.status === "error" ? "text-destructive" : "text-primary"} />
                )}
                <div className="text-sm">
                  {bundleBusy && (
                    <span className="font-medium" data-testid="bundle-progress">
                      Building your {({ pdf: "PDF", epub: "EPUB", docx: "Word" }[bundle.bundle_format] || "PDF")} bundle…
                      {bundle.progress?.total
                        ? ` Publication ${Math.min((bundle.progress.done || 0) + 1, bundle.progress.total)} of ${bundle.progress.total}`
                        : " preparing"}
                      {bundle.progress?.current ? ` — ${bundle.progress.current}` : ""}
                    </span>
                  )}
                  {bundle.status === "ready" && (
                    <span className="font-medium text-foreground">
                      Your {({ pdf: "PDF", epub: "EPUB", docx: "Word" }[bundle.bundle_format] || "PDF")} bundle is ready — one file per publication, in a single ZIP.
                    </span>
                  )}
                  {bundle.status === "error" && (
                    <span className="text-destructive">{bundle.error || "The bundle could not be built."}</span>
                  )}
                  {bundle.status === "expired" && (
                    <span className="text-muted-foreground">Your previous bundle link expired — rebuild it below.</span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {bundle.status === "ready" && bundle.download_path && (
                  <Button
                    size="sm"
                    className="rounded-sm"
                    onClick={() => triggerDownload(`${ORIGIN}${bundle.download_path}`, bundle.filename)}
                    data-testid="bundle-download-button"
                  >
                    <Download size={15} className="mr-2" /> Download ZIP
                  </Button>
                )}
                {(bundle.status === "error" || bundle.status === "expired") && (
                  <Button size="sm" className="rounded-sm" onClick={() => startBundle(bundle.bundle_format || "pdf")} disabled={bundleBusy} data-testid="bundle-retry-button">
                    Rebuild
                  </Button>
                )}
              </div>
              </div>
              {bundleBusy && bundle.progress?.total > 0 && (() => {
                const pct = Math.min(100, Math.round(((bundle.progress.done || 0) / bundle.progress.total) * 100));
                return (
                  <div data-testid="bundle-progress-bar-wrap">
                    <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{bundle.progress.done || 0} of {bundle.progress.total} publications</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
                        style={{ width: `${Math.max(3, pct)}%` }}
                        data-testid="bundle-progress-bar"
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {pausedCount > 0 && (
            <div
              className="mb-6 flex items-center justify-between gap-4 rounded-sm border border-amber-500/40 bg-amber-500/10 px-5 py-4"
              data-testid="resume-all-banner"
            >
              <div className="flex items-center gap-3">
                <PauseCircle size={20} className="text-amber-600" />
                <div className="text-sm">
                  <span className="font-medium text-amber-800">
                    {pausedCount} generation job{pausedCount === 1 ? "" : "s"} paused
                  </span>
                  <span className="text-amber-700/80"> — likely due to the AI daily spend limit. Resume once it resets.</span>
                </div>
              </div>
              <Button
                size="sm"
                className="rounded-sm shrink-0"
                onClick={resumeAll}
                disabled={resuming}
                data-testid="resume-all-button"
              >
                {resuming ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Play size={15} className="mr-2" />}
                Resume all
              </Button>
            </div>
          )}
          {projects === null ? (
            <div className="py-24 text-center text-muted-foreground">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="rounded-md border border-dashed border-border grid-lines py-20 text-center" data-testid="onboarding-empty">
              <div className="mx-auto flex max-w-md flex-col items-center px-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Upload size={26} />
                </div>
                <h2 className="mt-5 font-display text-2xl font-semibold tracking-tight">Ingest your first knowledge base</h2>
                <p className="mt-2 text-muted-foreground">
                  Drop in a CSV index (modules, submodules, titles, prompts) or a prompt — Espresso
                  structures it and processes it into a finished publication.
                </p>
                <Button onClick={startIngest} disabled={ingesting} className="mt-6 rounded-sm px-8" data-testid="onboarding-ingest">
                  {ingesting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                  Ingest a knowledge base (CSV)
                </Button>
                <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                  <button onClick={() => setOpen(true)} className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="empty-new-project">
                    Start from a prompt
                  </button>
                  <span className="text-muted-foreground/40">·</span>
                  <button onClick={loadDemo} disabled={loadingDemo} className="inline-flex items-center text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="empty-load-demo">
                    {loadingDemo ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
                    Try the demo
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/project/${p.id}`)}
                  className="group flex flex-col rounded-sm border border-border bg-card p-6 text-left transition-colors hover:border-primary/50"
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-semibold leading-tight">{p.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                      {p.purpose || "No description"}
                    </p>
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                    <span>
                      {p.doc_count} docs · {p.approved_count} approved
                    </span>
                    <ArrowRight
                      size={16}
                      className="text-primary transition-transform group-hover:translate-x-1"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
